import React from 'react'
import { Tooltip } from '@mui/material'
import { AddRounded, DarkMode as DarkModeIcon, LightMode as LightModeIcon, Search as SearchIcon, UndoRounded, RefreshRounded } from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { api } from '../function/axios'
import { formatDate } from '../function/date'
import { useAdminTheme } from '../hook/useAdminTheme'
import { ComposeMessageV2, Level, LevelIcon, LEVEL_TONE } from '../component/messages-v2/ComposeMessageV2'
import { paragraphText } from '../container/marketing/campaign/paragraphFormat'
import l from './SubscribersPageV2.module.css'
import s from '../component/messages-v2/MessagesV2.module.css'

/*
 * Messages to users (new in v2 — no classic page): what was sent, to how
 * many, how many read it and clicked its button, and a new one. Sent through
 * the backend's admin-messages module — stored for every recipient (the
 * dashboard's bell), delivered live to an open dashboard, no Firebase.
 */

type Message = {
  id: number
  title: string
  body: string
  level: Level
  display?: 'quiet' | 'bubble'
  slides?: unknown[] | null
  actionName: string | null
  audience: { type: 'all' | 'packages' | 'users'; packageIds?: number[]; userIds?: number[] }
  recipientsCount: number
  read: number
  clicked: number
  adminLogin: string | null
  expiresAt: string | null
  /** a scheduled one goes out then; sentAt stays null until it does */
  scheduledAt?: string | null
  sentAt?: string | null
  isCancelled: boolean
  createdTime: string
}

const audienceLabel = (a: Message['audience']) =>
  a?.type === 'all' ? 'All customers' : a?.type === 'packages' ? `${a.packageIds?.length || 0} package${a.packageIds?.length === 1 ? '' : 's'}` : `${a?.userIds?.length || 0} chosen user${a?.userIds?.length === 1 ? '' : 's'}`
const waiting = (m: Message) => !!m.scheduledAt && !m.sentAt && !m.isCancelled
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—')

export function MessagesPageV2() {
  const { theme, toggleTheme } = useAdminTheme()
  const [rows, setRows] = React.useState<Message[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [composing, setComposing] = React.useState(false)
  const [toCancel, setToCancel] = React.useState<Message | null>(null)
  const [cancelAllAsk, setCancelAllAsk] = React.useState(false)

  const load = React.useCallback(async () => {
    try {
      const { data } = await api({ method: 'GET', url: '/admin-messages', params: { offset: 0, limit: 200 } })
      setRows(data?.list || [])
    } catch {
      setRows([])
    }
  }, [])
  React.useEffect(() => { void load() }, [load])

  async function cancel(m: Message) {
    try {
      await api({ method: 'DELETE', url: `/admin-messages/${m.id}` })
      setRows((r) => (r ? r.map((x) => (x.id === m.id ? { ...x, isCancelled: true } : x)) : r))
    } catch { /* the api layer reports it */ }
  }

  async function cancelAll() {
    try {
      await api({ method: 'POST', url: '/admin-messages/withdraw-all' })
    } catch { /* the api layer reports it */ }
    void load()
  }

  const all = rows || []
  const now = Date.now()
  const live = all.filter((m) => !m.isCancelled && !waiting(m) && (!m.expiresAt || new Date(m.expiresAt).getTime() > now))
  const scheduled = all.filter(waiting)
  const sent = all.reduce((n, m) => n + (m.recipientsCount || 0), 0)
  const read = all.reduce((n, m) => n + (m.read || 0), 0)
  const q = query.trim().toLowerCase()
  const list = all.filter((m) => !q || m.title.toLowerCase().includes(q) || paragraphText(m.body).toLowerCase().includes(q))

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Customers</small><b>Messages</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search messages" />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>Messages</small><b>{rows ? all.length : '—'}</b><span>{rows ? `${live.length} shown now${scheduled.length ? ` · ${scheduled.length} scheduled` : ''}` : ''}</span></div>
          <div className={l.kpi}><small>Delivered</small><b>{rows ? sent.toLocaleString() : '—'}</b><span>to customers’ bells</span></div>
          <div className={l.kpi}><small>Read</small><b>{rows ? pct(read, sent) : '—'}</b><span>{rows ? `${read.toLocaleString()} opened` : ''}</span></div>
          <div className={l.kpi}><small>Clicked</small><b>{rows ? all.reduce((n, m) => n + (m.clicked || 0), 0).toLocaleString() : '—'}</b><span>on a message’s button</span></div>
        </div>

        <div className={l.sec}>
          <span className={s.secTitle}>Sent</span>
          <div className={l.spacer} />
          <Tooltip title="Withdraw every message still shown and cancel the scheduled ones">
            <span>
              <button type="button" className={s.ghost} onClick={() => setCancelAllAsk(true)} disabled={!live.length && !scheduled.length}>
                <UndoRounded sx={{ fontSize: 17 }} />Withdraw all
              </button>
            </span>
          </Tooltip>
          <Tooltip title="Reload"><button type="button" className={s.iconGhost} onClick={() => { void load() }} aria-label="Reload"><RefreshRounded sx={{ fontSize: 17 }} /></button></Tooltip>
          <button type="button" className={s.primary} onClick={() => setComposing(true)}><AddRounded sx={{ fontSize: 17 }} />New message</button>
        </div>

        <div className={l.card}>
          <div className={l.scroll}>
            {rows === null ? <div className={l.empty}>Loading…</div> : !list.length ? (
              <div className={l.empty}>{q ? 'Nothing matches the search' : 'No messages yet — send the first one'}</div>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Message</th>
                    <th>To</th>
                    <th>Read</th>
                    <th>Clicked</th>
                    <th>Sent</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((m) => {
                    const expired = !!m.expiresAt && new Date(m.expiresAt).getTime() <= now
                    const pending = waiting(m)
                    const status = m.isCancelled ? (m.scheduledAt && !m.sentAt ? 'Cancelled' : 'Withdrawn') : pending ? 'Scheduled' : expired ? 'Expired' : 'Shown'
                    return (
                      <tr key={m.id} className={m.isCancelled ? s.rowOff : ''}>
                        <td>
                          <div className={s.msgCell}>
                            <span className={s.msgIcon} style={{ ['--tone' as any]: LEVEL_TONE[m.level] }}><LevelIcon level={m.level} size={17} /></span>
                            <div className={s.msgText}>
                              <b>{m.title}{m.display === 'bubble' && <em className={s.displayTag}>bubble</em>}{!!m.slides?.length && <em className={s.displayTag}>{m.slides.length} slide{m.slides.length === 1 ? '' : 's'}</em>}</b>
                              <span>{paragraphText(m.body).replace(/\s+/g, ' ').slice(0, 120)}</span>
                            </div>
                          </div>
                        </td>
                        <td className={l.nowrap}>{audienceLabel(m.audience)}<span className={l.sub}>{pending ? 'counted when sent' : `${m.recipientsCount.toLocaleString()} customers`}</span></td>
                        <td className={l.nowrap}>
                          <b>{pct(m.read, m.recipientsCount)}</b>
                          <div className={s.bar}><i style={{ width: pct(m.read, m.recipientsCount) === '—' ? 0 : pct(m.read, m.recipientsCount) }} /></div>
                        </td>
                        <td className={l.nowrap}>{m.actionName ? <>{m.clicked.toLocaleString()}<span className={l.sub}>“{m.actionName}”</span></> : <span className={l.muted}>no button</span>}</td>
                        <td className={l.nowrap}>
                          {pending ? <>for {formatDate(m.scheduledAt as string, 'dd.MM.yyyy HH:mm')}</> : formatDate(m.sentAt || m.createdTime, 'dd.MM.yyyy HH:mm')}
                          <span className={l.sub}>{m.adminLogin || ''}</span>
                        </td>
                        <td className={l.nowrap}>
                          <span className={l.status}><span className={l.dot} style={{ background: status === 'Shown' ? 'var(--ok)' : status === 'Scheduled' ? 'var(--warn)' : 'var(--text3)' }} />{status}</span>
                          {m.expiresAt && !m.isCancelled && <span className={l.sub}>{expired ? 'ended' : 'until'} {formatDate(m.expiresAt, 'dd.MM.yyyy')}</span>}
                        </td>
                        <td>
                          {!m.isCancelled && !expired && (
                            <Tooltip title={pending ? 'Cancel — it will not be sent' : 'Withdraw — remove it from every bell'}>
                              <button type="button" className={s.iconGhost} onClick={() => setToCancel(m)} aria-label="Withdraw"><UndoRounded sx={{ fontSize: 17 }} /></button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {composing && <ComposeMessageV2 theme={theme} onClose={() => setComposing(false)} onSent={() => { void load() }} />}
      {cancelAllAsk && (
        <ConfirmV2
          header={`Withdraw all ${live.length + scheduled.length} messages? ${live.length} disappear from the customers’ bells${scheduled.length ? `, ${scheduled.length} scheduled will not be sent` : ''}.`}
          onYes={() => { void cancelAll() }}
          onClose={() => setCancelAllAsk(false)}
        />
      )}
      {toCancel && (
        <ConfirmV2
          header={waiting(toCancel)
            ? `Cancel “${toCancel.title}”? It will not be sent.`
            : `Withdraw “${toCancel.title}”? It disappears from ${toCancel.recipientsCount.toLocaleString()} customers’ bells.`}
          onYes={() => { void cancel(toCancel) }}
          onClose={() => setToCancel(null)}
        />
      )}
    </PrivatePage>
  )
}
